import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CalculatorDataProvider } from './context/CalculatorDataContext';
import { GlobalCalculatorSettingsProvider } from './context/GlobalCalculatorSettingsContext';
import { ProfileProvider } from './context/ProfileContext';
import { MobilePreviewProvider } from './context/MobilePreviewContext';
import SuperCalculatorLayout from './components/SuperCalculatorLayout';
import PurchasePlanner from './pages/calculators/PurchasePlanner';
import AffordabilityCalculator from './pages/calculators/AffordabilityCalculator';
import BuyingCalculator from './pages/calculators/BuyingCalculator';
import SellingCalculator from './pages/calculators/SellingCalculator';
import DecouplingCalculator from './pages/calculators/DecouplingCalculator';
import ProfileManager from './pages/ProfileManager';
import CubeTransition, { type CubeFace } from './components/CubeTransition';
import { LinkedCalculatorProvider, useLinkedCalculator } from './context/LinkedCalculatorContext';
import { buildUnauthorizedUrl, isDevLoginSessionLossCode, signedFetch } from '../super-picker/services/api';
import {
  initAnalytics,
  logCalculatorViewed,
  setAnalyticsCea,
  type SuperCalculatorKey,
} from './services/analytics';

type ActiveKey = 'purchase-planner' | 'affordability' | 'profile-manager' | 'buying-calculator' | 'selling-calculator' | 'decoupling-calculator';

type LoginUser = {
  Name?: string;
  Photo?: string;
  role?: string;
  CEANumber?: string;
  canAccessSellingCalculator?: boolean;
  canAccessDecouplingCalculator?: boolean;
};

function toTrackingCalculator(activeKey: ActiveKey): SuperCalculatorKey {
  if (activeKey === 'purchase-planner') return 'purchase_planner';
  if (activeKey === 'affordability') return 'affordability';
  if (activeKey === 'buying-calculator') return 'buying_calculator';
  if (activeKey === 'selling-calculator') return 'selling_calculator';
  if (activeKey === 'decoupling-calculator') return 'decoupling_calculator';
  return 'profile_manager';
}

function getRouteContext(pathname: string): { basePath: string; activeKey: ActiveKey | null } {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const inModuleNamespace = normalizedPath.startsWith('/super-calculator');
  const basePath = inModuleNamespace ? '/super-calculator' : '';
  const localPath = inModuleNamespace ? normalizedPath.replace('/super-calculator', '') || '/' : normalizedPath;

  // Default landing (/super-calculator) resolves to Maximizer (affordability).
  if (localPath === '/affordability' || localPath === '/') {
    return { basePath, activeKey: 'affordability' };
  }
  if (localPath === '/purchase-planner') {
    return { basePath, activeKey: 'purchase-planner' };
  }
  if (localPath === '/mortgage-loan') {
    return { basePath, activeKey: 'affordability' };
  }
  if (localPath === '/profile-manager') {
    return { basePath, activeKey: 'profile-manager' };
  }
  if (localPath === '/buying-calculator') {
    return { basePath, activeKey: 'buying-calculator' };
  }
  if (localPath === '/selling-calculator') {
    return { basePath, activeKey: 'selling-calculator' };
  }
  if (localPath === '/decoupling-calculator') {
    return { basePath, activeKey: 'decoupling-calculator' };
  }
  return { basePath, activeKey: null };
}

// ── ?redirect=<key> deep link ────────────────────────────────────────────────
// External entry points (EDMs, Transformation Office links) point at the stable
// /super-calculator URL and choose the destination with ?redirect=. Only the keys
// below are consumed here — anything else (e.g. ?redirect=training, handled in
// SuperCalculatorLayout) is left on the URL untouched.
const REDIRECT_PARAM_ROUTES: Record<string, ActiveKey> = {
  buying: 'buying-calculator',
  selling: 'selling-calculator',
  maximizer: 'affordability',
  planner: 'purchase-planner',
  decoupling: 'decoupling-calculator',
};

const LINKED_KEYS = new Set<ActiveKey>(['buying-calculator', 'selling-calculator', 'affordability', 'decoupling-calculator']);

function BorrowerSyncBridge({ activeKey }: { activeKey: string }) {
  const { notifyActiveCalculator } = useLinkedCalculator();
  useEffect(() => {
    notifyActiveCalculator(activeKey);
  }, [activeKey, notifyActiveCalculator]);
  return null;
}

const getPath = (basePath: string, segment: string): string =>
  basePath ? `${basePath}/${segment}` : `/${segment}`;

export function SuperCalculatorApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath, activeKey } = getRouteContext(location.pathname);
  // Resolve ?redirect= to the target calculator path, with the param consumed so a
  // later back/forward or reload doesn't bounce the user off the page they're on.
  const redirectTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const key = (params.get('redirect') || '').trim().toLowerCase();
    const target = REDIRECT_PARAM_ROUTES[key];
    if (!target) return null;
    params.delete('redirect');
    const query = params.toString();
    return `${getPath(basePath, target)}${query ? `?${query}` : ''}${location.hash}`;
  }, [location.search, location.hash, basePath]);
  const [accessState, setAccessState] = useState<'checking' | 'granted' | 'denied'>('checking');
  const [loginUser, setLoginUser] = useState<LoginUser | null>(null);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    let mounted = true;

    const verifyAccess = async () => {
      try {
        const response = await signedFetch('/api/super-calculator/user/me');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const errorCode = typeof data?.code === 'string' ? data.code : undefined;
          const devLoginRedirectHandled = response.status === 401 && isDevLoginSessionLossCode(errorCode);
          if (mounted) {
            setAccessState('denied');
            if (!devLoginRedirectHandled) {
              window.location.replace(
                buildUnauthorizedUrl(errorCode || 'NO_SUPER_CALCULATOR_ACCESS', { app: 'super-calculator' })
              );
            }
          }
          return;
        }
        if (mounted) {
          const ceaNumber = data.ceaNumber ?? data.CEANumber;
          setAnalyticsCea(ceaNumber);
          setAccessState('granted');
          setLoginUser({
            Name: data.name ?? data.Name,
            Photo: data.photo ?? data.Photo,
            role: data.role ?? data.Role,
            CEANumber: ceaNumber,
            canAccessSellingCalculator: data.canAccessSellingCalculator === true,
            canAccessDecouplingCalculator: data.canAccessDecouplingCalculator === true,
          });
        }
      } catch {
        if (mounted) {
          setAccessState('denied');
          window.location.replace(
            buildUnauthorizedUrl('NO_SUPER_CALCULATOR_ACCESS', { app: 'super-calculator' })
          );
        }
      }
    };

    verifyAccess();

    return () => {
      mounted = false;
    };
  }, []);

  // Derived here rather than after the early returns below, because cubeFaces needs it.
  const isAdmin = loginUser?.role?.trim().toUpperCase() === 'ADMIN';
  // Admins, Super Accelerators and DECOUPLE-CALC holders — resolved by /user/me.
  const canAccessDecouplingCalculator = isAdmin || loginUser?.canAccessDecouplingCalculator === true;

  const cubeFaces: CubeFace[] = useMemo(
    () => {
      // The suite is a branching loop, not a ring:
      //
      //   Maximizer  <->  Buying  <->  Selling     <-> Maximizer
      //                           <->  Decoupling  <-> Maximizer
      //
      // Buying is where the journey forks: having priced a purchase, the agent either
      // sells the existing property or decouples it. Both routes end back at the
      // Maximizer with the freed-up funds — and that last step walks both ways, so the
      // Maximizer's back arrow returns to whichever branch the agent came through
      // instead of stranding them at the start of the journey. Selling and Decoupling
      // are access-gated, so both forks are built from whichever of them this user can
      // actually reach — with neither, Buying has no forward step and the Maximizer has
      // nothing behind it.
      const canSell = loginUser?.canAccessSellingCalculator === true;
      const forward = [
        ...(canSell ? ['selling-calculator'] : []),
        ...(canAccessDecouplingCalculator ? ['decoupling-calculator'] : []),
      ];

      return [
        {
          key: 'affordability',
          label: 'Max Affordability Calculator',
          content: <AffordabilityCalculator />,
          // Both ends of the fork lead back here, so they are also what sits behind it.
          prev: forward.length > 0 ? forward : null,
          next: 'buying-calculator',
        },
        {
          key: 'buying-calculator',
          label: 'Buying Calculator',
          shortLabel: 'Buying',
          content: <BuyingCalculator isAdmin={isAdmin} />,
          prev: 'affordability',
          next: forward.length > 0 ? forward : null,
        },
        ...(canSell
          ? [{
              key: 'selling-calculator',
              label: 'Selling Calculator',
              shortLabel: 'Selling',
              content: <SellingCalculator />,
              prev: 'buying-calculator',
              next: 'affordability',
            }]
          : []),
        ...(canAccessDecouplingCalculator
          ? [{
              key: 'decoupling-calculator',
              label: 'Decoupling Calculator',
              shortLabel: 'Decoupling',
              content: <DecouplingCalculator />,
              prev: 'buying-calculator',
              next: 'affordability',
            }]
          : []),
      ];
    },
    [loginUser?.canAccessSellingCalculator, canAccessDecouplingCalculator, isAdmin],
  );

  const handleCubeNavigate = useCallback(
    (key: string) => navigate(getPath(basePath, key)),
    [basePath, navigate],
  );

  useEffect(() => {
    if (accessState !== 'granted' || !activeKey || redirectTo) return;
    logCalculatorViewed(toTrackingCalculator(activeKey), loginUser?.CEANumber);
  }, [accessState, activeKey, redirectTo, loginUser?.CEANumber]);

  // Handled before the access check so the deep link lands on the target calculator
  // without first flashing the default Maximizer view.
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!activeKey) {
    const fallback = basePath ? `${basePath}/affordability` : '/affordability';
    return <Navigate to={fallback} replace />;
  }

  if (accessState === 'checking') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Checking Super Calculator access...</Typography>
      </Box>
    );
  }

  if (accessState === 'denied') {
    return null;
  }

  // Buying and Selling are open to every signed-in agent (same as the Maximizer); the
  // Selling flag is still read from /user/me so the server stays the source of truth.
  // Only Decoupling is truly gated here.
  const canAccessSellingCalculator = loginUser?.canAccessSellingCalculator === true;

  if (activeKey === 'selling-calculator' && !canAccessSellingCalculator) {
    return <Navigate to={getPath(basePath, 'affordability')} replace />;
  }

  if (activeKey === 'decoupling-calculator' && !canAccessDecouplingCalculator) {
    return <Navigate to={getPath(basePath, 'affordability')} replace />;
  }

  const isLinked = LINKED_KEYS.has(activeKey);

  return (
    <CalculatorDataProvider>
      <GlobalCalculatorSettingsProvider>
        <ProfileProvider>
          <MobilePreviewProvider>
            <SuperCalculatorLayout
              basePath={basePath}
              activeKey={activeKey}
              loginUser={loginUser}
              isAdmin={isAdmin}
              canAccessSellingCalculator={canAccessSellingCalculator}
              canAccessDecouplingCalculator={canAccessDecouplingCalculator}
            >
              {isLinked ? (
                <LinkedCalculatorProvider>
                  <BorrowerSyncBridge activeKey={activeKey} />
                  <CubeTransition
                    faces={cubeFaces}
                    activeKey={activeKey}
                    onNavigate={handleCubeNavigate}
                  />
                </LinkedCalculatorProvider>
              ) : (
                <>
                  {activeKey === 'purchase-planner' && <PurchasePlanner />}
                  {activeKey === 'profile-manager' && <ProfileManager />}
                </>
              )}
            </SuperCalculatorLayout>
          </MobilePreviewProvider>
        </ProfileProvider>
      </GlobalCalculatorSettingsProvider>
    </CalculatorDataProvider>
  );
}
